<template>
  <form @submit.prevent="handleSubmit">
    <div class="form-field">
      <input-text
        elem-id="soReportAttributeName"
        v-model="formData.so_ra_name"
        :label="t('Nome do Atributo de Laudo')"
        :placeholder="t('Digite o nome do atributo de laudo')"
        type="text"
        required
        :validation="validateName"
        fluid
        data-testid="so-report-attributes-name-input"
      />
      <small v-if="errors.so_ra_name" class="error-message">{{ errors.so_ra_name }}</small>
    </div>

    <div class="form-field">
      <SelectList
        elem-id="soReportAttributeType"
        v-model="formData.so_ra_type"
        :itens-select="attributeTypeOptions"
        :label="t('Tipo')"
        :placeholder="t('Selecione o tipo')"
        ng-required="true"
        :validation="validateType"
        :auto-select-first="false"
      />
      <small v-if="errors.so_ra_type" class="error-message">{{ errors.so_ra_type }}</small>
    </div>

    <div v-if="isEditMode && formData.so_ra_type === 'select'" class="options-section">
      <h3 class="section-title">{{ t('Opções do Atributo') }}</h3>

      <div v-if="attributeOptions.length === 0" class="empty-state">
        {{ t('Nenhuma opção cadastrada') }}
      </div>

      <div v-else class="options-list">
        <div
          v-for="option in attributeOptions"
          :key="option.so_rao_id"
          class="option-row"
        >
          <div class="option-info">
            <div class="option-main">
              <span class="option-value">{{ option.so_rao_value }}</span>
            </div>
            <span class="option-order">{{ t('Ordem') }}: {{ option.so_rao_display_order ?? 0 }}</span>
          </div>
          <div class="option-actions">
            <Button
              icon="pi pi-pencil"
              severity="info"
              size="small"
              text
              type="button"
              data-testid="so-report-attributes-edit-option-btn"
              @click="handleEditOption(option)"
            />
            <Button
              icon="pi pi-trash"
              severity="danger"
              size="small"
              text
              type="button"
              data-testid="so-report-attributes-delete-option-btn"
              @click="handleDeleteOption(option.so_rao_id)"
            />
          </div>
        </div>
      </div>

      <div class="options-form">
        <div class="flex-container">
          <div class="flex-item-half">
            <div class="form-field">
              <input-text
                elem-id="soReportAttributeOptionValue"
                data-testid="so-report-attributes-option-value-input"
                v-model="newOption.so_rao_value"
                :label="t('Valor da Opção')"
                :placeholder="t('Digite o valor da opção')"
                type="text"
                required
                :validation="validateOptionValue"
              />
              <small v-if="optionErrors.so_rao_value" class="error-message">{{ optionErrors.so_rao_value }}</small>
            </div>
          </div>

          <div class="flex-item-half">
            <div class="form-field">
              <input-number
                elem-id="soReportAttributeOptionDisplayOrder"
                data-testid="so-report-attributes-option-display-order-input"
                v-model="newOption.so_rao_display_order"
                :label="t('Ordem de Exibição')"
                :placeholder="t('Digite a ordem')"
                :min="0"
                :maxFractionDigits="0"
                :validation="validateOptionDisplayOrder"
              />
              <small v-if="optionErrors.so_rao_display_order" class="error-message">{{ optionErrors.so_rao_display_order }}</small>
            </div>
          </div>
        </div>

        <div class="options-actions">
          <Button
            v-if="editingOptionId"
            :label="t('Cancelar')"
            icon="pi pi-times"
            size="small"
            severity="secondary"
            text
            type="button"
            data-testid="so-report-attributes-cancel-edit-option-btn"
            @click="cancelEditOption"
          />
          <Button
            :label="isSavingOption ? t('Salvando') : editingOptionId ? t('Salvar Opção') : t('Adicionar Opção')"
            :icon="editingOptionId ? 'pi pi-check' : 'pi pi-plus'"
            size="small"
            :loading="isSavingOption"
            type="button"
            data-testid="so-report-attributes-add-option-btn"
            @click="handleAddOption"
          />
        </div>
      </div>
    </div>
  </form>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import SelectList from '../../inputs/SelectList.vue';
import { useSOReportAttributes } from '../../composables/useSOReportAttributes';
import { eventBus } from '../../eventBus';
import { log, warn as logWarn, error as logError } from '../../utils/DebugLogger';

type Id = number | string;

interface Props {
  attributeId?: Id | null;
  isEditMode?: boolean;
}

interface ApiError extends Error {
  response?: { data?: { error?: string } };
}

interface SelectOption {
  ID: number | string;
  Name: string;
}

interface AttributeRow {
  so_ra_id?: Id;
  so_ra_name?: string;
  so_ra_type?: string;
  [key: string]: unknown;
}

interface ReportAttributeOption {
  so_rao_id?: Id;
  so_rao_attribute_id?: Id;
  so_rao_value?: string;
  so_rao_display_order?: number | null;
}

interface SavePayload {
  so_ra_name: string;
  so_ra_type: string;
  so_ra_id?: number;
}

interface SaveResponse {
  dados?: { so_ra_id?: Id; id?: Id };
}

const props = withDefaults(defineProps<Props>(), {
  attributeId: null,
  isEditMode: false
});

const emit = defineEmits<{
  'submit-success': [payload: { so_ra_id: number | null }];
  'submit-error': [error: unknown];
}>();

const { t } = useI18n();

const { fetchAll, save, fetchOptions, saveOption, removeOption } = useSOReportAttributes();

const attributeIdNumber = computed<number | null>(() => {
  if (!props.attributeId) return null;
  const num = typeof props.attributeId === 'string' ? parseInt(props.attributeId, 10) : props.attributeId;
  log('SOReportAttributesForm', 'attributeIdNumber', 'Converted ID', { original: props.attributeId, converted: num });
  return num;
});

const attributeTypeOptions = ref<SelectOption[]>([
  { ID: 'text', Name: 'text' },
  { ID: 'textarea', Name: 'textarea' },
  { ID: 'photo', Name: 'photo' },
  { ID: 'select', Name: 'select' },
  { ID: 'checkbox', Name: 'checkbox' }
]);

const formData = reactive<{ so_ra_name: string; so_ra_type: string }>({
  so_ra_name: '',
  so_ra_type: ''
});

const errors = reactive<{ so_ra_name: string; so_ra_type: string }>({
  so_ra_name: '',
  so_ra_type: ''
});

const newOption = reactive<{
  so_rao_value: string;
  so_rao_display_order: number | null;
}>({
  so_rao_value: '',
  so_rao_display_order: 0
});

const optionErrors = reactive<{
  so_rao_value: string;
  so_rao_display_order: string;
}>({
  so_rao_value: '',
  so_rao_display_order: ''
});

const isLoading = ref<boolean>(false);
const isLoadingOptions = ref<boolean>(false);
const isSavingOption = ref<boolean>(false);
const attributeOptions = ref<ReportAttributeOption[]>([]);
const editingOptionId = ref<Id | null>(null);

const validateName = (): boolean => {
  return Boolean(formData.so_ra_name) && formData.so_ra_name.trim() !== '' && formData.so_ra_name.length <= 255;
};

const validateType = (): boolean => {
  return Boolean(formData.so_ra_type) && formData.so_ra_type.trim() !== '';
};

const validateOptionValue = (): boolean => {
  return Boolean(newOption.so_rao_value) && newOption.so_rao_value.trim() !== '';
};

const validateOptionDisplayOrder = (): boolean => {
  return newOption.so_rao_display_order == null || newOption.so_rao_display_order >= 0;
};

const loadOptions = async (): Promise<void> => {
  if (!attributeIdNumber.value || formData.so_ra_type !== 'select') {
    attributeOptions.value = [];
    return;
  }

  log('SOReportAttributesForm', 'loadOptions', 'Loading report attribute options', attributeIdNumber.value);
  isLoadingOptions.value = true;

  try {
    attributeOptions.value = await fetchOptions(attributeIdNumber.value) as ReportAttributeOption[];
    log('SOReportAttributesForm', 'loadOptions', 'Loaded report attribute options', attributeOptions.value);
  } catch (error) {
    logError('SOReportAttributesForm', 'loadOptions', 'Error loading report attribute options', error);
    const message = error instanceof Error ? error.message : '';
    eventBus.emit('globalMessage.error', t('Erro ao carregar opções do atributo de laudo') + ': ' + message);
  } finally {
    isLoadingOptions.value = false;
  }
};

const loadForEdit = async (): Promise<void> => {
  if (!attributeIdNumber.value) {
    logWarn('SOReportAttributesForm', 'loadForEdit', 'No attributeId provided');
    return;
  }

  log('SOReportAttributesForm', 'loadForEdit', 'Loading report attribute for edit', attributeIdNumber.value);
  isLoading.value = true;

  try {
    const attributes = (await fetchAll()) as unknown as AttributeRow[];
    const attribute = attributes.find(entry => Number(entry.so_ra_id) === Number(attributeIdNumber.value));
    log('SOReportAttributesForm', 'loadForEdit', 'Loaded data', attribute);

    if (!attribute) {
      throw new Error('Dados do atributo de laudo não encontrados');
    }

    formData.so_ra_name = attribute.so_ra_name || '';
    formData.so_ra_type = attribute.so_ra_type || '';

    clearErrors();
    await loadOptions();
  } catch (error) {
    const err = error as ApiError;
    logError('SOReportAttributesForm', 'loadForEdit', 'Error loading report attribute', {
      attributeId: attributeIdNumber.value,
      error: err.message,
      stack: err.stack,
      response: err.response
    });

    const errorMessage = err.response?.data?.error || err.message || 'Erro desconhecido';
    eventBus.emit('globalMessage.error', t('Erro ao carregar atributo de laudo') + ': ' + errorMessage);
    emit('submit-error', err);
  } finally {
    isLoading.value = false;
  }
};

const validateForm = (): boolean => {
  log('SOReportAttributesForm', 'validateForm', 'Validating form');
  clearErrors();
  let isValid = true;

  if (!formData.so_ra_name || formData.so_ra_name.trim() === '') {
    errors.so_ra_name = t('Campo obrigatório');
    isValid = false;
  } else if (formData.so_ra_name.length > 255) {
    errors.so_ra_name = t('Nome muito longo') + ' (max 255)';
    isValid = false;
  }

  if (!formData.so_ra_type || formData.so_ra_type.trim() === '') {
    errors.so_ra_type = t('Campo obrigatório');
    isValid = false;
  }

  log('SOReportAttributesForm', 'validateForm', 'Validation result', isValid);
  return isValid;
};

const validateOptionForm = (): boolean => {
  log('SOReportAttributesForm', 'validateOptionForm', 'Validating option form');
  clearOptionErrors();
  let isValid = true;

  if (!newOption.so_rao_value || newOption.so_rao_value.trim() === '') {
    optionErrors.so_rao_value = t('Campo obrigatório');
    isValid = false;
  }

  if (newOption.so_rao_display_order != null && newOption.so_rao_display_order < 0) {
    optionErrors.so_rao_display_order = t('Campo inválido');
    isValid = false;
  }

  log('SOReportAttributesForm', 'validateOptionForm', 'Validation result', isValid);
  return isValid;
};

const clearErrors = (): void => {
  errors.so_ra_name = '';
  errors.so_ra_type = '';
};

const clearOptionErrors = (): void => {
  optionErrors.so_rao_value = '';
  optionErrors.so_rao_display_order = '';
};

const resetOptionForm = (): void => {
  log('SOReportAttributesForm', 'resetOptionForm', 'Resetting option form');
  editingOptionId.value = null;
  newOption.so_rao_value = '';
  newOption.so_rao_display_order = 0;
  clearOptionErrors();
};

const handleEditOption = (option: ReportAttributeOption): void => {
  editingOptionId.value = option.so_rao_id ?? null;
  newOption.so_rao_value = option.so_rao_value || '';
  newOption.so_rao_display_order = option.so_rao_display_order ?? 0;
  clearOptionErrors();
};

const cancelEditOption = (): void => {
  resetOptionForm();
};

const resetForm = (): void => {
  log('SOReportAttributesForm', 'resetForm', 'Resetting form');
  formData.so_ra_name = '';
  formData.so_ra_type = '';
  attributeOptions.value = [];
  clearErrors();
  resetOptionForm();
};

const handleAddOption = async (): Promise<void> => {
  log('SOReportAttributesForm', 'handleAddOption', 'Adding new report attribute option');

  if (!attributeIdNumber.value) {
    logWarn('SOReportAttributesForm', 'handleAddOption', 'Cannot add option without attribute ID');
    return;
  }

  if (!validateOptionForm()) {
    logWarn('SOReportAttributesForm', 'handleAddOption', 'Option validation failed');
    return;
  }

  isSavingOption.value = true;

  try {
    const baseOption = {
      so_rao_value: newOption.so_rao_value.trim(),
      so_rao_display_order: newOption.so_rao_display_order ?? 0
    };

    const optionPayload = editingOptionId.value
      ? { ...baseOption, so_rao_id: Number(editingOptionId.value) }
      : { ...baseOption, so_rao_attribute_id: attributeIdNumber.value ?? undefined };

    await saveOption(optionPayload);

    eventBus.emit('globalMessage.success',
      editingOptionId.value ? t('Opção do atributo atualizada com sucesso') : t('Opção do atributo criada com sucesso')
    );
    resetOptionForm();
    await loadOptions();
  } catch (error) {
    logError('SOReportAttributesForm', 'handleAddOption', 'Error saving report attribute option', error);
    const message = error instanceof Error ? error.message : '';
    eventBus.emit('globalMessage.error', t('Erro ao salvar opção do atributo de laudo') + ': ' + message);
  } finally {
    isSavingOption.value = false;
  }
};

const handleDeleteOption = async (optionId: Id | undefined): Promise<void> => {
  log('SOReportAttributesForm', 'handleDeleteOption', 'Deleting report attribute option', optionId);

  if (optionId === undefined) return;

  try {
    await removeOption(Number(optionId));
    eventBus.emit('globalMessage.success', t('Opção do atributo deletada com sucesso'));
    await loadOptions();
  } catch (error) {
    logError('SOReportAttributesForm', 'handleDeleteOption', 'Error deleting report attribute option', error);
    const message = error instanceof Error ? error.message : '';
    eventBus.emit('globalMessage.error', t('Erro ao deletar opção do atributo de laudo') + ': ' + message);
  }
};

const handleSubmit = async (): Promise<void> => {
  log('SOReportAttributesForm', 'handleSubmit', 'Form submitted');

  if (!validateForm()) {
    logWarn('SOReportAttributesForm', 'handleSubmit', 'Validation failed');
    return;
  }

  isLoading.value = true;

  try {
    const payload: SavePayload = {
      so_ra_name: formData.so_ra_name.trim(),
      so_ra_type: formData.so_ra_type
    };

    let savedId: number | null = attributeIdNumber.value;
    if (props.isEditMode && attributeIdNumber.value) {
      payload.so_ra_id = attributeIdNumber.value;
      log('SOReportAttributesForm', 'handleSubmit', 'Updating report attribute', payload);
      await save(payload);
      eventBus.emit('globalMessage.success', t('Atributo de laudo atualizado com sucesso'));
    } else {
      log('SOReportAttributesForm', 'handleSubmit', 'Creating report attribute', payload);
      const response = (await save(payload)) as SaveResponse | undefined;
      savedId = Number(response?.dados?.so_ra_id || response?.dados?.id || 0) || null;
      eventBus.emit('globalMessage.success', t('Atributo de laudo criado com sucesso'));
    }

    emit('submit-success', { so_ra_id: savedId });
    resetForm();
  } catch (error) {
    const err = error as Error;
    logError('SOReportAttributesForm', 'handleSubmit', 'Error submitting form', err);
    eventBus.emit('globalMessage.error',
      props.isEditMode
        ? t('Erro ao atualizar atributo de laudo') + ': ' + err.message
        : t('Erro ao criar atributo de laudo') + ': ' + err.message
    );
    emit('submit-error', err);
  } finally {
    isLoading.value = false;
  }
};

watch(() => props.attributeId, async (newId) => {
  log('SOReportAttributesForm', 'watch:attributeId', 'attributeId changed', { newId, isEditMode: props.isEditMode });
  if (newId && props.isEditMode) {
    await nextTick();
    await loadForEdit();
  }
});

watch(() => formData.so_ra_type, async (newType, oldType) => {
  log('SOReportAttributesForm', 'watch:so_ra_type', 'Type changed', { oldType, newType });
  if (newType !== 'select') {
    attributeOptions.value = [];
    resetOptionForm();
    return;
  }

  if (props.isEditMode && attributeIdNumber.value) {
    await loadOptions();
  }
});

onMounted(() => {
  log('SOReportAttributesForm', 'mounted', 'Component mounted', {
    attributeId: props.attributeId,
    attributeIdNumber: attributeIdNumber.value,
    isEditMode: props.isEditMode
  });

  if (props.isEditMode && attributeIdNumber.value) {
    loadForEdit();
  }
});

defineExpose({
  loadForEdit,
  handleSubmit,
  resetForm,
  validateForm
});
</script>

<style scoped>
.form-field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.error-message {
  color: var(--p-red-500);
  font-size: 0.875rem;
  margin-top: 0.25rem;
  display: block;
}

.options-section {
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid var(--p-content-border-color, #e5e7eb);
}

.section-title {
  margin: 0 0 1rem 0;
  font-size: 1rem;
  font-weight: 600;
}

.empty-state {
  margin-bottom: 1rem;
  color: #6c757d;
}

.options-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.option-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.75rem;
  border: 1px solid var(--p-content-border-color, #e5e7eb);
  border-radius: 6px;
}

.option-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.option-main {
  display: flex;
  align-items: center;
  gap: 8px;
}

.option-value {
  font-weight: 500;
}

.option-order {
  color: #6c757d;
  font-size: 0.875rem;
}

.option-actions {
  display: flex;
  gap: 4px;
  flex-shrink: 0;
}

.options-form {
  margin-top: 1rem;
}

.options-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.flex-container {
  display: flex;
  gap: 1rem;
}

.flex-item-half {
  flex: 1;
}

@media (max-width: 768px) {
  .flex-container {
    flex-direction: column;
    gap: 0;
  }

  .option-row {
    flex-direction: column;
    align-items: flex-start;
  }

  .options-actions {
    justify-content: stretch;
  }
}
</style>
